
import { defineComponent, PropType, ref, watch, computed, onBeforeUnmount } from 'vue'

interface TableColumn {
    key: string
    title: string
    width?: number | string
    fixed?: 'left' | 'right'
    align?: 'left' | 'center' | 'right'
    sortable?: boolean
    formatter?: string
}

function getPageScale(): number {
    const el = document.documentElement
    const inline = el.style.zoom
    if (inline) {
        const s = String(inline)
        if (s.endsWith('%')) {
            const n = parseFloat(s)
            if (Number.isFinite(n) && n > 0) return n / 100
        }
        const n = parseFloat(s)
        if (Number.isFinite(n) && n > 0) return n
    }
    const computedZoom = window.getComputedStyle(el).zoom
    if (computedZoom && computedZoom !== 'normal') {
        const n = parseFloat(computedZoom)
        if (Number.isFinite(n) && n > 0) return n
    }
    const vvScale = window.visualViewport?.scale
    if (vvScale && Number.isFinite(vvScale) && vvScale > 0 && vvScale !== 1) {
        return vvScale
    }
    return 1
}

export default defineComponent({
    name: 'HeaderSettingsPanel',
    props: {
        visible: {
            type: Boolean,
            default: false
        },
        columns: {
            type: Array as PropType<TableColumn[]>,
            default: () => []
        },
        defaultColumns: {
            type: Array as PropType<TableColumn[]>,
            default: () => []
        },
        theme: {
            type: String as PropType<'light' | 'dark'>,
            default: 'dark'
        }
    },
    emits: ['update:visible', 'apply', 'cancel'],
    setup(props, { emit }) {
        const localColumns = ref<TableColumn[]>([])
        const dragIndex = ref<number | null>(null)
        const hoverIndex = ref<number | null>(null)
        const isDragging = ref(false)
        const columnListRef = ref<HTMLElement | null>(null)
        const panelRef = ref<HTMLElement | null>(null)
        const overlayRef = ref<HTMLElement | null>(null)

        const panelCompensateStyle = computed(() => {
            const scale = getPageScale()
            if (Math.abs(scale - 1) < 0.001) return undefined
            return { zoom: `${100 / scale}%` }
        })

        let ghostEl: HTMLElement | null = null
        let offsetX = 0
        let offsetY = 0
        let rafId = 0
        let mouseX = 0
        let mouseY = 0
        let activePointerId: number | null = null
        let pointerCaptureEl: HTMLElement | null = null

        const syncColumnsFromProps = () => {
            localColumns.value = props.columns.map(col => ({ ...col }))
        }

        watch(() => props.visible, (newVisible) => {
            if (newVisible) {
                syncColumnsFromProps()
            } else {
                finishDrag()
            }
        })

        watch(() => props.columns, () => {
            if (props.visible && !isDragging.value) {
                syncColumnsFromProps()
            }
        }, { deep: true })

        const getTagElements = () => {
            if (!columnListRef.value) return [] as HTMLElement[]
            return Array.from(columnListRef.value.querySelectorAll('.column-tag')) as HTMLElement[]
        }

        const findHoverIndex = (clientX: number, clientY: number): number | null => {
            if (dragIndex.value === null) return null
            const tags = getTagElements()
            for (let i = 0; i < tags.length; i++) {
                if (i === dragIndex.value) continue
                const rect = tags[i].getBoundingClientRect()
                if (clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom) {
                    return i
                }
            }
            return null
        }

        const getVisualScale = (el: HTMLElement): number => {
            const layoutWidth = el.offsetWidth
            if (!layoutWidth) return 1
            return el.getBoundingClientRect().width / layoutWidth
        }

        const viewportToPanelLocal = (clientX: number, clientY: number) => {
            const panel = panelRef.value
            if (!panel) return { x: 0, y: 0 }
            const panelRect = panel.getBoundingClientRect()
            const scale = getVisualScale(panel)
            return {
                x: (clientX - panelRect.left) / scale,
                y: (clientY - panelRect.top) / scale
            }
        }

        const createGhost = (sourceEl: HTMLElement, clientX: number, clientY: number) => {
            const panel = panelRef.value
            if (!panel) return

            const rect = sourceEl.getBoundingClientRect()
            offsetX = clientX - rect.left
            offsetY = clientY - rect.top

            const start = viewportToPanelLocal(rect.left, rect.top)
            const cs = window.getComputedStyle(sourceEl)

            const ghost = document.createElement('div')
            ghost.classList.add('drag-ghost')
            ghost.textContent = sourceEl.textContent?.trim() || ''
            Object.assign(ghost.style, {
                position: 'absolute',
                left: `${start.x}px`,
                top: `${start.y}px`,
                width: `${sourceEl.offsetWidth}px`,
                height: `${sourceEl.offsetHeight}px`,
                boxSizing: 'border-box',
                padding: cs.padding,
                fontSize: cs.fontSize,
                fontFamily: cs.fontFamily,
                fontWeight: cs.fontWeight,
                lineHeight: cs.lineHeight,
                color: cs.color,
                border: '1px solid #1890ff',
                borderRadius: cs.borderRadius,
                background: 'rgba(24, 144, 255, 0.1)',
                boxShadow: '0 4px 12px rgba(24, 144, 255, 0.35)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
                zIndex: '20',
                margin: '0',
                opacity: '0.92',
                userSelect: 'none',
                whiteSpace: 'nowrap',
                transition: 'none'
            })
            panel.appendChild(ghost)
            ghostEl = ghost
        }

        const removeGhost = () => {
            if (ghostEl && ghostEl.parentNode) {
                ghostEl.parentNode.removeChild(ghostEl)
            }
            ghostEl = null
        }

        const moveGhost = () => {
            if (!ghostEl) return
            const pos = viewportToPanelLocal(mouseX - offsetX, mouseY - offsetY)
            ghostEl.style.left = `${pos.x}px`
            ghostEl.style.top = `${pos.y}px`
        }

        const rafTick = () => {
            rafId = 0
            moveGhost()
            if (!isDragging.value || dragIndex.value === null) return
            hoverIndex.value = findHoverIndex(mouseX, mouseY)
        }

        const handlePointerMove = (e: PointerEvent) => {
            if (!isDragging.value || activePointerId === null || e.pointerId !== activePointerId) return
            mouseX = e.clientX
            mouseY = e.clientY
            if (!rafId) {
                rafId = requestAnimationFrame(rafTick)
            }
        }

        const performReorder = (from: number, to: number) => {
            if (from === to) return
            const arr = localColumns.value.slice()
            const [item] = arr.splice(from, 1)
            arr.splice(to, 0, item)
            localColumns.value = arr
        }

        const finishDrag = () => {
            if (rafId) {
                cancelAnimationFrame(rafId)
                rafId = 0
            }

            if (isDragging.value && dragIndex.value !== null && hoverIndex.value !== null) {
                performReorder(dragIndex.value, hoverIndex.value)
            }

            const captureId = activePointerId
            const captureEl = pointerCaptureEl

            isDragging.value = false
            dragIndex.value = null
            hoverIndex.value = null
            activePointerId = null
            pointerCaptureEl = null

            if (captureEl && captureId !== null) {
                try {
                    captureEl.releasePointerCapture(captureId)
                } catch {
                    // ignore
                }
            }

            removeGhost()
            document.removeEventListener('pointermove', handlePointerMove, true)
            document.removeEventListener('pointerup', handlePointerUp, true)
            document.removeEventListener('pointercancel', handlePointerUp, true)
        }

        function handlePointerUp(e: PointerEvent) {
            if (!isDragging.value || activePointerId === null || e.pointerId !== activePointerId) return
            e.preventDefault()
            e.stopPropagation()
            finishDrag()
        }

        const handleOverlayPointerDown = (e: PointerEvent) => {
            e.preventDefault()
            e.stopPropagation()
        }

        const handlePointerDown = (e: PointerEvent, index: number) => {
            if (e.button !== 0 || isDragging.value) return
            e.preventDefault()
            e.stopPropagation()

            isDragging.value = true
            dragIndex.value = index
            hoverIndex.value = index
            activePointerId = e.pointerId
            mouseX = e.clientX
            mouseY = e.clientY

            pointerCaptureEl = overlayRef.value || panelRef.value || (e.currentTarget as HTMLElement)
            try {
                pointerCaptureEl.setPointerCapture(e.pointerId)
            } catch {
                pointerCaptureEl = null
            }

            createGhost(e.currentTarget as HTMLElement, e.clientX, e.clientY)

            document.addEventListener('pointermove', handlePointerMove, true)
            document.addEventListener('pointerup', handlePointerUp, true)
            document.addEventListener('pointercancel', handlePointerUp, true)
        }

        onBeforeUnmount(() => {
            finishDrag()
        })

        const handleApply = () => {
            if (isDragging.value) finishDrag()
            emit('apply', localColumns.value)
            emit('update:visible', false)
        }

        const handleCancel = () => {
            if (isDragging.value) finishDrag()
            emit('cancel')
            emit('update:visible', false)
        }

        const handleReset = () => {
            if (isDragging.value) finishDrag()
            localColumns.value = props.defaultColumns.map(col => ({ ...col }))
        }

        return {
            localColumns,
            dragIndex,
            hoverIndex,
            isDragging,
            columnListRef,
            panelRef,
            overlayRef,
            panelCompensateStyle,
            handlePointerDown,
            handleOverlayPointerDown,
            handleApply,
            handleCancel,
            handleReset
        }
    }
})
